import { TOrderTracking } from '../domains/Order';
import getConfig from 'next/config';

const isBrazeDebug = () => {
  const {
    publicRuntimeConfig: { BRAZE_DEBUG },
  } = getConfig();
  return BRAZE_DEBUG === 'true';
};

const brazeService = (onInitialized?: () => void) => {
  const {
    publicRuntimeConfig: { BRAZE_API_KEY, BRAZE_SDK_ENDPOINT },
  } = getConfig();

  import(
    /* webpackExports: ["initialize", "openSession", "requestPushPermission", "subscribeToInAppMessage", "isPushSupported", "isPushPermissionGranted", "isPushBlocked", "showInAppMessage", "FullScreenMessage", "logCustomEvent"] */
    '@braze/web-sdk'
  ).then(
    ({
      initialize,
      openSession,
      requestPushPermission,
      subscribeToInAppMessage,
      isPushSupported,
      isPushPermissionGranted,
      isPushBlocked,
      showInAppMessage,
      logCustomEvent,
      FullScreenMessage,
    }) => {
      initialize(BRAZE_API_KEY, {
        baseUrl: BRAZE_SDK_ENDPOINT,
        allowUserSuppliedJavascript: true,
        safariWebsitePushId: 'web.ie.distilled.donedeal',
        // Set to true only when debugging
        enableLogging: isBrazeDebug(),
      });
      subscribeToInAppMessage(function (inAppMessage) {
        // check if message is not a control variant
        if (inAppMessage instanceof FullScreenMessage) {
          // access the key-value pairs, defined as `extras`
          const keyValuePairs = inAppMessage.extras || {};
          // check the value of our key `msg-id` defined in the Braze dashboard
          if (keyValuePairs['msg-id'] === 'push-primer') {
            // We don't want to display the soft push prompt to users on browsers
            // that don't support push, or if the user has already granted/blocked permission
            if (
              isPushSupported() === false ||
              isPushPermissionGranted() ||
              isPushBlocked()
            ) {
              // do not call `showInAppMessage`
              return;
            }

            // user is eligible to receive the native prompt
            // register a click handler on one of the two buttons
            if (inAppMessage.buttons[0]) {
              // Prompt the user when the first button is clicked
              inAppMessage.buttons[0].subscribeToClickedEvent(function () {
                requestPushPermission();
              });
            }
          }
        }
        // show the in-app message now
        showInAppMessage(inAppMessage);
      });
      openSession();
      logCustomEvent('prime-for-push');

      // Notify that initialization is complete
      onInitialized?.();
    },
  );
};

const changeUser = (userId: string) => {
  import(
    /* webpackExports: ["changeUser"] */
    '@braze/web-sdk'
  ).then(({ changeUser }) => {
    changeUser(userId.toString());
  });
};

enum Event {
  SAVE_AD = 'Save Ad',
  PLACE_AD = 'Place Ad',
  EDIT_AD = 'Edit Ad',
  SEND_MESSAGE = 'Send Message',
  SEND_DEALER_ENQUIRY = 'Send Dealer Enquiry',
  REVEAL_PHONE = 'Reveal Phone',
  RELIST_AD = 'Relist Ad',
  FREE_HISTORY_CHECK_AD_VIEW = 'Ad view with Free Hc',
  FREE_HISTORY_CHECK_AD_SAVE = 'Saved ad with Free Hc',
  VIEW_AD = 'View Ad',
  VIEW_AD_20K = 'View Ad 20k',
  VIEW_AD_EV = 'View Ad EV',
  WHATSAPP = 'Whatsapp',
  SAVE_AD_20K = 'Save Ad 20k',
  SAVE_AD_EV = 'Save Ad EV',
}

type TLogEvent<T = Event> = {
  name: T;
  properties: TEventProperties<T>;
};

type TEventProperties<T = Event> = T extends Event.SAVE_AD
  ? TSaveAdEventProperties
  : T extends Event.PLACE_AD
  ? TPlaceAdEventProperties
  : T extends Event.EDIT_AD
  ? TEditAdEventProperties
  : T extends Event.SEND_MESSAGE
  ? TSendMessageEventProperties
  : T extends Event.SEND_DEALER_ENQUIRY
  ? TSendDealerEnquiryEventProperties
  : T extends Event.REVEAL_PHONE
  ? TRevealPhoneEventProperties
  : T extends Event.RELIST_AD
  ? TRelistAdEventProperties
  : T extends Event.FREE_HISTORY_CHECK_AD_VIEW
  ? AdViewEventProperties
  : T extends Event.FREE_HISTORY_CHECK_AD_SAVE
  ? AdSaveEventProperties
  : T extends
      | Event.VIEW_AD
      | Event.VIEW_AD_20K
      | Event.VIEW_AD_EV
      | Event.WHATSAPP
      | Event.SAVE_AD_20K
      | Event.SAVE_AD_EV
  ? CarAdEventProperties
  : never;

type TSaveAdEventProperties = {
  Platform: 'Web';
  subsection?: string;
  'seller type'?: string;
};

type TPlaceAdEventProperties = {
  ad_id?: string;
  category?: string;
  location?: string;
  platform?: string | null;
  price?: string;
};

type TEditAdEventProperties = {
  ad_id: number;
  category: string;
  location: string;
  platform: 'Web';
  price: string;
};

type TSendMessageEventProperties = {
  Platform: 'Web';
  subsection?: string;
};

type TSendDealerEnquiryEventProperties = {
  Platform: 'Web';
  subsection?: string;
};

type TRevealPhoneEventProperties = {
  Platform: 'Web';
  subsection?: string;
  sellertype?: string;
};

type TRelistAdEventProperties = {
  Platform: 'Web';
  subsection?: string;
};

// Shared type for all car ADP events. Platform is added by the log function.
type CarAdEventProperties = {
  Platform: 'Web';
  ad_id: string;
  ad_price: string;
  seller_type: string;
};

type CarAdEventInput = Omit<CarAdEventProperties, 'Platform'>;

// Braze expects Platform in PascalCase. The other properties are custom and defined by us.
type AdViewEventProperties = {
  Platform: 'Web';
  ad_id: string;
  category: string;
  location: string;
  ad_price: string;
};

type AdViewEventInput = Omit<AdViewEventProperties, 'Platform'>;

type AdSaveEventProperties = {
  Platform: 'Web';
  ad_id: string;
  category: string;
  location: string;
  ad_price: string;
};

type AdSaveEventInput = Omit<AdSaveEventProperties, 'Platform'>;

const logEvent = <T extends string>({ name, properties }: TLogEvent<T>) => {
  import(
    /* webpackExports: ["logCustomEvent"] */
    '@braze/web-sdk'
  ).then(({ logCustomEvent, requestImmediateDataFlush }) => {
    logCustomEvent(name, properties);
    // When debugging we want to request immediate data flushing. In production, braze batches events
    // before firing them for efficiency, but these events can not be seen directly in custom event
    // logs. Immediately flushing post log ensures one record per event which is viewable via the
    // Braze dashboard.
    isBrazeDebug() && requestImmediateDataFlush();
  });
};

const logBrazeSaveAdEvent = (subsection?: string, sellerType?: string) => {
  logEvent({
    name: Event.SAVE_AD,
    properties: {
      Platform: 'Web',
      subsection: subsection,
      'seller type': sellerType,
    },
  });
};

const logBrazePlaceAdEvent = (orderTrackingData: TOrderTracking) => {
  if (orderTrackingData) {
    const adId = orderTrackingData.adId?.toString();
    const category = orderTrackingData.adSection?.toString();
    const location = orderTrackingData.county?.toString();
    const price = orderTrackingData.price.toString();
    logEvent({
      name: Event.PLACE_AD,
      properties: {
        platform: 'Web',
        ad_id: adId,
        category: category,
        location: location,
        price: price,
      },
    });
  }
};

const logBrazeEditAdEvent = (
  adId: number,
  subsection: string,
  location: string,
  price: string,
) => {
  logEvent({
    name: Event.EDIT_AD,
    properties: {
      platform: 'Web',
      ad_id: adId,
      category: subsection,
      location: location,
      price: price,
    },
  });
};

const logBrazeSendMessageEvent = (subsection?: string) => {
  logEvent({
    name: Event.SEND_MESSAGE,
    properties: {
      Platform: 'Web',
      subsection: subsection,
    },
  });
};

const logBrazeSendDealerEnquiryEvent = (subsection?: string) => {
  logEvent({
    name: Event.SEND_DEALER_ENQUIRY,
    properties: {
      Platform: 'Web',
      subsection: subsection,
    },
  });
};

const logBrazeRevealPhoneEvent = (subsection?: string, sellerType?: string) => {
  logEvent({
    name: Event.REVEAL_PHONE,
    properties: {
      Platform: 'Web',
      subsection: subsection,
      sellertype: sellerType,
    },
  });
};

const logBrazeRelistAdEvent = (subsection: string) => {
  logEvent({
    name: Event.RELIST_AD,
    properties: {
      Platform: 'Web',
      subsection: subsection,
    },
  });
};

const logBrazeFreeHistoryCheckAdView = (adViewEvent: AdViewEventInput) => {
  const { ad_id, category, location, ad_price } = adViewEvent;
  logEvent({
    name: Event.FREE_HISTORY_CHECK_AD_VIEW,
    properties: {
      Platform: 'Web',
      ad_id,
      category,
      location,
      ad_price,
    },
  });
};

const logBrazeFreeHistoryCheckAdSave = (adSaveEvent: AdSaveEventInput) => {
  const { ad_id, category, location, ad_price } = adSaveEvent;
  logEvent({
    name: Event.FREE_HISTORY_CHECK_AD_SAVE,
    properties: {
      Platform: 'Web',
      ad_id,
      category,
      location,
      ad_price,
    },
  });
};

const logBrazeViewAdEvent = (input: CarAdEventInput) => {
  logEvent({
    name: Event.VIEW_AD,
    properties: { Platform: 'Web', ...input },
  });
};

const logBrazeViewAd20kEvent = (input: CarAdEventInput) => {
  logEvent({
    name: Event.VIEW_AD_20K,
    properties: { Platform: 'Web', ...input },
  });
};

const logBrazeViewAdEvEvent = (input: CarAdEventInput) => {
  logEvent({
    name: Event.VIEW_AD_EV,
    properties: { Platform: 'Web', ...input },
  });
};

const logBrazeWhatsAppEvent = (input: CarAdEventInput) => {
  logEvent({
    name: Event.WHATSAPP,
    properties: { Platform: 'Web', ...input },
  });
};

const logBrazeSaveAd20kEvent = (input: CarAdEventInput) => {
  logEvent({
    name: Event.SAVE_AD_20K,
    properties: { Platform: 'Web', ...input },
  });
};

const logBrazeSaveAdEvEvent = (input: CarAdEventInput) => {
  logEvent({
    name: Event.SAVE_AD_EV,
    properties: { Platform: 'Web', ...input },
  });
};

export {
  brazeService,
  logBrazeSaveAdEvent,
  logBrazePlaceAdEvent,
  logBrazeEditAdEvent,
  logBrazeSendMessageEvent,
  logBrazeSendDealerEnquiryEvent,
  logBrazeRevealPhoneEvent,
  logBrazeRelistAdEvent,
  changeUser,
  logBrazeFreeHistoryCheckAdView,
  logBrazeFreeHistoryCheckAdSave,
  logBrazeViewAdEvent,
  logBrazeViewAd20kEvent,
  logBrazeViewAdEvEvent,
  logBrazeWhatsAppEvent,
  logBrazeSaveAd20kEvent,
  logBrazeSaveAdEvEvent,
};
